/**
 * Entitlement resolution — the single place that decides what a tenant may have.
 *
 * Every entitlement is a row in `tenant_grants`: a Paddle subscription, an
 * operator's concession, or the in-app trial, distinguished only by `source`.
 * This function turns those rows into an answer. Nothing else may.
 *
 * ## Why this exists
 *
 * The plan used to live in denormalized columns on `tenants` (plan,
 * plan_expires_at, trial_started_at, max_templates, extra_template_count) with
 * **two writers**: the Paddle webhook rebuilt them from subscription rows, and
 * the admin console wrote them directly with no row behind them. A cache with
 * two writers and no owner drifts by construction, and it did — an operator
 * could edit `max_templates` on a lapsed tenant and see nothing happen, because
 * the enforced value was derived elsewhere.
 *
 * So entitlement is derived at the point of use, exactly as `isQuotaEnforced()`
 * and `isTemplateLive()` already are. Nothing is written back.
 *
 * ## Consequences worth knowing
 *
 * - **The free tier is not a row.** It is the floor returned when no grant
 *   applies. Materialising it per tenant would be thousands of rows restating a
 *   constant.
 * - **A lapse needs no fallback.** An expired grant simply stops being in force;
 *   there is no "drop back to Free" branch to get wrong.
 * - **A negotiated custom cap is an `extra_templates` grant** with
 *   `source='manual'` and no end date. There is no separate concept, so there is
 *   no second code path to keep in step.
 *
 * ## Seats
 *
 * A seat plan is two grant kinds, `seat_starter` and `seat_business`, whose
 * `quantity` is a seat count. This function answers only what a seat means for
 * the *tenant*: how many are in force, and that any seat at all gives the Pro
 * template base. Who holds one is `resolveSeats()`; what a holder may do is
 * `resolveAccess()`. **A seat is not Pro** — `pro`, `trialing` and everything
 * the legacy adapter derives are untouched by it.
 *
 * ## What is deliberately NOT here
 *
 * Document packs have their own table, `document_packs`. A pack is a balance
 * drawn down one document at a time and reversed per transaction on a refund,
 * which the in-force model does not describe: a document is spent, not expired.
 * Folding packs in would mean two sources for the same balance — precisely the
 * disease this table cures.
 */

import type { GrantKind, SeatCounts, SeatTier } from '../entities'
import {
  ACTIVE_SUBSCRIPTION_STATUSES,
  FREE_MAX_TEMPLATES,
  MAX_EXTRA_TEMPLATES_TOTAL,
  PRO_MAX_TEMPLATES,
  TRIAL_MAX_TEMPLATES,
} from './constants'

/** Both seat tiers, in the order a list shows them. */
export const SEAT_TIERS: readonly SeatTier[] = ['starter', 'business']

/** The grant kind that carries each tier's seats. */
export const SEAT_GRANT_KIND: Record<SeatTier, GrantKind> = {
  starter: 'seat_starter',
  business: 'seat_business',
}

/** The tier a grant kind carries seats for, or null if it is not a seat kind. */
export function seatTierOfGrantKind(kind: string): SeatTier | null {
  if (kind === 'seat_starter') return 'starter'
  if (kind === 'seat_business') return 'business'
  return null
}

/**
 * Template base for a tenant *without* Pro access.
 *
 * Any seat in force gives the Pro base; no seat gives the Free one. Exported
 * because two callers need the same answer — the resolver below, and
 * `getEffectiveMaxTemplates()`, whose lapsed-plan branch would otherwise drop a
 * tenant holding seats to the Free cap the day their Pro ended.
 */
export function templateBaseWithoutPro(seats: SeatCounts | null | undefined): number {
  return seats && seats.starter + seats.business > 0 ? PRO_MAX_TEMPLATES : FREE_MAX_TEMPLATES
}

/**
 * The fields resolution needs. Any row shape carrying these can be passed in,
 * so the resolver is testable without a database and usable from either worker.
 */
export interface ResolvableGrant {
  kind: GrantKind
  /** Present on Paddle rows; the adapter surfaces them for display. */
  billing_period?: 'monthly' | 'quarterly' | 'annual' | null
  paddle_subscription_id?: string | null
  quantity: number
  starts_at: number
  ends_at: number | null
  source: 'paddle' | 'manual' | 'trial'
  status: string
  revoked_at: number | null
}

export interface Entitlement<G extends ResolvableGrant = ResolvableGrant> {
  /** Pro access right now — paid, manual, or trialling. */
  pro: boolean
  /** True when the *only* thing granting pro is a trial. */
  trialing: boolean
  /**
   * When pro access ends. `null` means perpetual; `undefined` means no pro
   * access at all — a distinction the caller needs, since "never expires" and
   * "already gone" are opposites.
   */
  proUntil: number | null | undefined
  /** Live template slots: plan base plus add-on grants. */
  maxTemplates: number
  /** Add-on slots only — what is paid for or was granted on top of the base. */
  extraTemplates: number
  /**
   * The part of `extraTemplates` an operator granted (`source = 'manual'`)
   * rather than a customer bought. Display only: the Billing page must not call
   * a concession "purchased".
   */
  extraTemplatesGranted: number
  /**
   * Seats in force per tier, summed over every grant of that tier — a monthly
   * and an annual subscription are two rows, and an operator's seats a third.
   * A count only: who sits in them is `resolveSeats()`.
   */
  seats: SeatCounts
  /**
   * Whether this tenant has *ever* held a trial grant, including expired and
   * revoked ones. A trial is once per tenant, so this cannot be answered from
   * the in-force set.
   */
  everTrialed: boolean
  /**
   * Whether this tenant has *ever* held any pro grant, in force or not.
   *
   * Distinguishes "lapsed" from "never had it", which the banners rely on: a
   * tenant who never subscribed must not be told their plan expired.
   */
  everPro: boolean
  /**
   * Whether any *paid or manual* pro grant has ever existed — a trial does not
   * count.
   *
   * Separate from `everPro` because the two drive different copy: a churned
   * paying customer must see "renew", a spent trialist must see "start Pro". Get
   * this wrong and a lapsed subscriber is shown the trial upsell.
   */
  everPaidPro: boolean
  /**
   * When pro access last ended, if it has. Null while pro is in force or if it
   * never was.
   *
   * The adapter needs a real date here: the legacy shape has one field for both
   * "expires at" and "expired at", and filling it with a sentinel made every
   * lapsed tenant render 01/01/1970 on the admin list and the support card.
   */
  proEndedAt: number | null
  /** When the trial began, from the trial grant. Null if there never was one. */
  trialStartedAt: number | null
  /** The grants in force, for display. Never used for the numbers above. */
  active: G[]
}

/**
 * The plan fields the existing checkers consume, as *derived* from grants.
 *
 * A deliberate seam rather than a rewrite of every signature. `TenantPlanFields`
 * has the shape of the legacy cache, and dozens of call sites across two workers
 * and four frontends read it. Rather than change all of them, this is the one
 * sanctioned way to construct it: pass the resolved entitlement, and every
 * downstream check — quota, template capacity, plan status — evaluates the
 * ledger without knowing the ledger exists.
 *
 * **Never hand-build these fields from a `tenants` row.** That is the bug this
 * whole change removes. The quota columns are passed through untouched because
 * they are operational policy, not entitlement: Paddle does not sell them.
 */
export function entitlementPlanFields<T extends LegacyQuotaFields>(
  ent: Entitlement,
  tenant: T,
): T & {
  plan: 'free' | 'pro'
  plan_expires_at: number | null
  trial_started_at: number | null
  max_templates: number
  extra_template_count: number
  extra_templates_granted: number
  billing_period: 'monthly' | 'quarterly' | 'annual' | null
  paddle_subscription_id: string | null
  seats: SeatCounts
} {
  const paddleGrant = ent.active.find((g) => g.kind === 'pro' && g.source === 'paddle')

  return {
    ...tenant,
    // `plan` is the *stored tier*, not current access — getPlanStatus derives
    // `isProExpired` from `plan === 'pro' && isExpired`, so a churned subscriber
    // must keep reading 'pro' or that flag can never fire and the renew banner,
    // the Billing CTA and the admin "Pro Expired" label all go dead. A spent
    // trialist is *not* 'pro': they would otherwise be offered a renewal for
    // something they never bought.
    plan: (ent.pro && !ent.trialing) || (!ent.pro && ent.everPaidPro) ? 'pro' : 'free',
    // With pro: null legitimately means perpetual. Without it, a tenant who once
    // had pro needs a *past* date so `isExpired` reads true, while one who never
    // did needs null — otherwise a virgin Free tenant is told their plan lapsed.
    plan_expires_at: ent.pro ? (ent.proUntil ?? null) : (ent.proEndedAt ?? (ent.everPro ? LAPSED : null)),
    // Set whenever a trial was *ever* held, not only while one is in force:
    // getPlanStatus derives canStartTrial from this being empty, so clearing it
    // on expiry would hand every past trialist a second trial.
    // From the trial grant, not the column: nothing writes trial_started_at any
    // more, so a trial begun after the cutover would have reported the sentinel.
    trial_started_at: ent.everTrialed
      ? (ent.trialStartedAt ?? tenant.trial_started_at ?? LAPSED)
      : null,
    max_templates: ent.maxTemplates,
    extra_template_count: ent.extraTemplates,
    // The operator-granted part of it, so a concession is not shown as bought.
    extra_templates_granted: ent.extraTemplatesGranted,
    // Derived too, for the same reason: nothing writes these columns any more, so
    // a consumer reading them off the row would lose the billing-interval label
    // and badge every new subscriber as hand-managed.
    billing_period: paddleGrant?.billing_period ?? null,
    paddle_subscription_id: paddleGrant?.paddle_subscription_id ?? null,
    // Carried so getEffectiveMaxTemplates() can keep a seat-holding tenant on
    // the Pro base after their Pro lapses: its lapsed branch rebuilds the cap
    // from a base plus extras, and without this the base it picks is Free.
    seats: ent.seats,
  }
}

/** The quota/policy fields the adapter passes through rather than derives. */
export interface LegacyQuotaFields {
  monthly_quota: number | null
  current_quota: number | null
  quota_reset_day_number: number | null
  enforce_quota: number
  trial_started_at?: number | null
}

/**
 * Last-resort "expired" timestamp, used only when a tenant held pro but no grant
 * records when it ended — a revoked-with-no-end-date row, for instance.
 * `proEndedAt` supplies a real date in every ordinary case, because rendering the
 * epoch put 01/01/1970 in front of operators.
 */
const LAPSED = 1

/**
 * Whether a grant is in force at `now`.
 *
 * `past_due` counts as in force deliberately: Paddle retries a failed payment
 * for days, and cutting a paying customer off during a card retry is worse than
 * carrying them. Paddle moves the subscription to `canceled` when it gives up.
 */
export function isGrantInForce(grant: ResolvableGrant, now: number): boolean {
  if (grant.revoked_at !== null) return false
  if (grant.starts_at > now) return false
  // Exclusive: a grant ending exactly now has ended. Matters at period
  // boundaries, where Paddle's renewal and our clock can coincide.
  if (grant.ends_at !== null && now >= grant.ends_at) return false
  return (ACTIVE_SUBSCRIPTION_STATUSES as readonly string[]).includes(grant.status)
}

/**
 * Resolve a tenant's entitlements from their grants.
 *
 * Pure and total: an empty list is a valid input and yields the Free tier.
 *
 * @param grants - All of the tenant's grants, in force or not
 * @param now - Evaluation time; injectable so a single request evaluates every
 *   entitlement on one clock rather than drifting between checks
 */
export function resolveEntitlements<G extends ResolvableGrant>(
  grants: readonly G[],
  now: number = Date.now(),
): Entitlement<G> {
  const active = grants.filter((g) => isGrantInForce(g, now))

  const proGrants = active.filter((g) => g.kind === 'pro')
  const pro = proGrants.length > 0

  // Trialling only if *every* pro grant is a trial. A trial overlapping a paid
  // grant is a paying customer, and billing them as a trialist would be wrong
  // in the expensive direction.
  const trialing = pro && proGrants.every((g) => g.source === 'trial')

  // Perpetual wins: a grant with no end date outlives any dated one, so a null
  // among the ends_at values is the answer rather than something to skip.
  let proUntil: number | null | undefined
  if (!pro) {
    proUntil = undefined
  } else if (proGrants.some((g) => g.ends_at === null)) {
    proUntil = null
  } else {
    proUntil = Math.max(...proGrants.map((g) => g.ends_at as number))
  }

  const extraGrants = active.filter((g) => g.kind === 'extra_templates')
  const extraTemplates = Math.min(
    MAX_EXTRA_TEMPLATES_TOTAL,
    extraGrants.reduce((sum, g) => sum + Math.max(0, g.quantity), 0),
  )
  // Within the cap, so purchased + granted never exceeds what is in force.
  const extraTemplatesGranted = Math.min(
    extraTemplates,
    extraGrants.filter((g) => g.source === 'manual').reduce((sum, g) => sum + Math.max(0, g.quantity), 0),
  )

  // Summed per tier, never capped against assignments: this is what was bought.
  const seats: SeatCounts = { starter: 0, business: 0 }
  for (const grant of active) {
    const tier = seatTierOfGrantKind(grant.kind)
    if (tier) seats[tier] += Math.max(0, grant.quantity)
  }

  // A trial is a smaller allowance than paid Pro, so the base depends on which
  // kind of pro access this is. Seats never lower it: a trialist who also holds
  // a seat gets the better of the two bases, whatever the constants become.
  const base = pro
    ? Math.max(trialing ? TRIAL_MAX_TEMPLATES : PRO_MAX_TEMPLATES, templateBaseWithoutPro(seats))
    : templateBaseWithoutPro(seats)

  return {
    pro,
    trialing,
    proUntil,
    maxTemplates: base + extraTemplates,
    extraTemplates,
    extraTemplatesGranted,
    seats,
    // Deliberately over the full list, not `active`: a spent trial must keep
    // barring a second one.
    everTrialed: grants.some((g) => g.kind === 'pro' && g.source === 'trial'),
    everPro: grants.some((g) => g.kind === 'pro'),
    everPaidPro: grants.some((g) => g.kind === 'pro' && g.source !== 'trial'),
    trialStartedAt:
      grants
        .filter((g) => g.kind === 'pro' && g.source === 'trial')
        .map((g) => g.starts_at)
        .sort((a, b) => a - b)[0] ?? null,
    proEndedAt: pro
      ? null
      : (() => {
          const ended = grants
            .filter((g) => g.kind === 'pro' && g.ends_at !== null && g.ends_at <= now)
            .map((g) => g.ends_at as number)
          return ended.length > 0 ? Math.max(...ended) : null
        })(),
    active,
  }
}
